import JSZip from "jszip";

import { CBCT_MAX_FILESIZE } from "~/common/constants";
import { fetchWithAuth } from "~/common/fetch";

const ARCHIVE_PATTERN = /\.(zip|7z|rar)$/i;

export type CbctValidationError = "notify.upload.E_FILE_TOO_BIG_CBCT" | "notify.upload.E_UNSUPPORTED_FORMAT_CBCT";

export function isCbctArchive(fileName: string): boolean {
  return ARCHIVE_PATTERN.test(fileName);
}

export function validateCbctFiles(files: File[]): CbctValidationError | null {
  if (files.reduce((sum, file) => sum + file.size, 0) > CBCT_MAX_FILESIZE) {
    return "notify.upload.E_FILE_TOO_BIG_CBCT";
  }

  if (files.some((file) => !ARCHIVE_PATTERN.test(file.name))) {
    return "notify.upload.E_UNSUPPORTED_FORMAT_CBCT";
  }

  return null;
}

export async function packCbctFiles(files: File[]): Promise<File[]> {
  const archives = files.filter((file) => ARCHIVE_PATTERN.test(file.name));
  const loose = files.filter((file) => !ARCHIVE_PATTERN.test(file.name));

  if (loose.length === 0) {
    return archives;
  }

  const zip = new JSZip();
  loose.forEach((file) => zip.file(file.name, file));
  const blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE", compressionOptions: { level: 6 } });
  const date = new Date().toISOString().split("T")[0];

  return [...archives, new File([blob], `${date}.cbct.zip`, { type: "application/zip" })];
}

export async function fetchCbctUploadLinks(files: File[], uuid: string, clinicId: number | null): Promise<Record<string, string>> {
  const res = await fetchWithAuth(`${process.env.API}/patients/media`, {
    method: "POST",
    body: JSON.stringify({
      uuid,
      clinic_id: clinicId,
      files: files.map((file) => ({ file_name: file.name, file_type: "dicom" })),
    }),
  });

  const data: unknown = await res.json();
  if (!res.ok) {
    throw data;
  }

  return data as Record<string, string>;
}
